import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useExpenses } from "@/hooks/useExpenses";
import { Loader2 } from "lucide-react";

interface QuickAddExpenseDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  defaultVehicleId?: string | null;
}

export const QuickAddExpenseDialog = ({
  open,
  onOpenChange,
  defaultVehicleId,
}: QuickAddExpenseDialogProps) => {
  const { vehicles, expenseTypes, paidByOptions, addExpense } = useExpenses();

  const today = new Date().toISOString().split("T")[0];

  const [amount, setAmount] = useState("");
  const [expenseName, setExpenseName] = useState("");
  const [vehicleId, setVehicleId] = useState<string>(defaultVehicleId || "");
  const [expenseType, setExpenseType] = useState("");
  const [expenseDate, setExpenseDate] = useState(today);
  const [mileage, setMileage] = useState("");
  const [paidBy, setPaidBy] = useState("");

  // Reset form when dialog opens
  const handleOpenChange = (nextOpen: boolean) => {
    if (nextOpen) {
      setAmount("");
      setExpenseName("");
      setVehicleId(defaultVehicleId || "");
      setExpenseType("");
      setExpenseDate(today);
      setMileage("");
      setPaidBy("");
    }
    onOpenChange(nextOpen);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const parsedAmount = parseFloat(amount);
    if (isNaN(parsedAmount) || parsedAmount <= 0) return;

    const parsedMileage = mileage ? parseFloat(mileage) : undefined;

    addExpense.mutate(
      {
        amount: parsedAmount,
        expense_name: expenseName || undefined,
        vehicle_id: vehicleId || null,
        expense_type: expenseType || undefined,
        expense_date: expenseDate,
        mileage: parsedMileage,
        paid_by: paidBy || undefined,
      },
      {
        onSuccess: () => handleOpenChange(false),
      }
    );
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Quick Add Expense</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Expense name — first field */}
          <div className="space-y-1.5">
            <Label htmlFor="qa-name">Expense Name</Label>
            <Input
              id="qa-name"
              placeholder="e.g. Car wash, Gas, Tire rotation"
              value={expenseName}
              onChange={(e) => setExpenseName(e.target.value)}
              autoFocus
            />
          </div>

          {/* Row 2: Date + Vehicle */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="qa-date">Date</Label>
              <Input
                id="qa-date"
                type="date"
                value={expenseDate}
                onChange={(e) => setExpenseDate(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label>Vehicle</Label>
              <Select value={vehicleId} onValueChange={setVehicleId}>
                <SelectTrigger>
                  <SelectValue placeholder="Select car" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="operations">Operations</SelectItem>
                  {vehicles.map((v) => (
                    <SelectItem key={v.id} value={v.id}>
                      {v.name || `${v.year} ${v.make} ${v.model}`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Row 3: Amount + Miles Driven */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="qa-amount">Amount *</Label>
              <Input
                id="qa-amount"
                type="number"
                step="0.01"
                min="0.01"
                placeholder="0.00"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="qa-mileage">Miles Driven</Label>
              <Input
                id="qa-mileage"
                type="number"
                step="1"
                min="0"
                placeholder="0"
                value={mileage}
                onChange={(e) => setMileage(e.target.value)}
              />
            </div>
          </div>

          {/* Row 4: Type + Paid By */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Type</Label>
              <Select value={expenseType} onValueChange={setExpenseType}>
                <SelectTrigger>
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  {expenseTypes.map((t) => (
                    <SelectItem key={t} value={t}>
                      {t}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Paid By</Label>
              <Select value={paidBy} onValueChange={setPaidBy}>
                <SelectTrigger>
                  <SelectValue placeholder="Select" />
                </SelectTrigger>
                <SelectContent>
                  {paidByOptions.map((p) => (
                    <SelectItem key={p} value={p}>
                      {p}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <Button
            className="w-full"
            disabled={!amount || parseFloat(amount) <= 0 || addExpense.isPending}
          >
            {addExpense.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : null}
            Add Expense
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
